import type { DropdownMenuItem } from '@nuxt/ui';
import type { MaybeRefOrGetter } from 'vue';
import type { CollectionFieldPath, PrimitiveType, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { useClipboard } from '@vueuse/core';
import { ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { serializeRawValue } from '#layers/data-model/public';
import { injectFilterSchemaContext, isFilterablePrimitivePath } from '#layers/filters/public';
import { injectListingContext } from '#layers/listing/components/Listing/context';
import { useCopyRawValueMenuItem } from '#layers/ui/public';

interface BuildCellMenuArgs {
	fieldPath: CollectionFieldPath;
	value: unknown;
	type: PrimitiveType;
}

export function useCellContextMenu(collection: MaybeRefOrGetter<ResolvedMonospaceCollection>) {
	const { t } = useI18n();
	const { copy } = useClipboard();
	const copied = ref(false);
	const listingContext = injectListingContext();
	const filterSchema = injectFilterSchemaContext();
	const buildCopyRawValueMenuItem = useCopyRawValueMenuItem();

	function copyValue(value: unknown, type: PrimitiveType) {
		copy(serializeRawValue(value, type));
		copied.value = true;
	}

	function resetCopied() {
		copied.value = false;
	}

	function buildCellMenuItems({ fieldPath, value, type }: BuildCellMenuArgs): DropdownMenuItem[] | undefined {
		if (value === undefined) return undefined;

		const items: DropdownMenuItem[] = [
			buildCopyRawValueMenuItem({
				copied,
				onCopy: () => copyValue(value, type),
			}),
		];

		const filterCollection = filterSchema.findCollectionById(toValue(collection).id);
		const filterable = Boolean(listingContext.addFieldFilterWithValue)
			&& filterCollection
			&& isFilterablePrimitivePath(fieldPath, filterCollection, filterSchema);

		if (filterable) {
			items.push({
				label: t('filters.filter_by_value'),
				icon: 'lucide:list-filter',
				onSelect: () => listingContext.addFieldFilterWithValue!(fieldPath, value),
			});
		}

		return items;
	}

	return { buildCellMenuItems, resetCopied };
}
